import React, { useState, useEffect } from 'react';
import { Key, ShieldCheck, Laptop, AlertCircle, Loader2, LogOut, CheckCircle2 } from 'lucide-react';
import { secureStorage } from '../lib/secureStorage';
import { motion, AnimatePresence } from 'motion/react';
import { auth, db, doc, getDoc, updateDoc, collection, addDoc, serverTimestamp } from '../firebase';
import { onAuthStateChanged } from '../firebase';

const LOCAL_DEV_USER_EMAIL_KEY = 'local_dev_user_email';
const LOCAL_API_TOKEN_KEY = 'app_auth_token';
const isLocalhost = () => ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
const hasLocalDevSession = () => isLocalhost() && !!(
  localStorage.getItem(LOCAL_API_TOKEN_KEY) || localStorage.getItem(LOCAL_DEV_USER_EMAIL_KEY)
);

interface LicenseGateProps {
  children: React.ReactNode;
}

export default function LicenseGate({ children }: LicenseGateProps) {
  const [licenseKey, setLicenseKey] = useState<string | null>(localStorage.getItem('app_license_key'));
  const [isValidated, setIsValidated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState('');
  const [validationData, setValidationData] = useState<any>(null);
  const [user, setUser] = useState<any>(null);
  const [loadingAuth, setLoadingAuth] = useState(true);

  const deviceId = secureStorage.getDeviceId();

  const logActivity = async (key: string, status: 'Success' | 'Failure', details?: string) => {
    try {
      let ipAddress = 'Unknown';
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        ipAddress = data.ip;
      } catch (e) {
        console.warn('Failed to fetch IP address', e);
      }

      const userAgent = navigator.userAgent;
      let deviceName = 'Unknown Device';
      
      if (/iPhone/i.test(userAgent)) deviceName = 'Apple iPhone';
      else if (/iPad/i.test(userAgent)) deviceName = 'Apple iPad';
      else if (/Macintosh/i.test(userAgent)) deviceName = 'Mac';
      else if (/Windows/i.test(userAgent)) deviceName = 'Windows PC';
      else if (/Samsung/i.test(userAgent)) deviceName = 'Samsung';
      else if (/Xiaomi/i.test(userAgent)) deviceName = 'Xiaomi';
      else if (/Android/i.test(userAgent)) deviceName = 'Android Device';
      else if (/Linux/i.test(userAgent)) deviceName = 'Linux device';

      await addDoc(collection(db, 'licenses', key, 'activities'), {
        timestamp: serverTimestamp(),
        ipAddress,
        deviceName,
        status,
        details: details || ''
      });
    } catch (err) {
      console.error('Failed to log license activity', err);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoadingAuth(false);
      if (currentUser) {
        setIsValidated(true); // Bypass for account users
      } else if (hasLocalDevSession()) {
        setIsValidated(true); // Bypass for local dev admin
      } else if (!licenseKey) {
        setIsValidated(false);
      }
    });
    return () => unsubscribe();
  }, [licenseKey]);

  const validateLicense = async (key: string) => {
    setLoading(true);
    setError(null);
    try {
      if (isLocalhost()) {
        const response = await fetch('/api/licenses/activate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            licenseKey: key,
            deviceId,
            deviceName: navigator.userAgent,
            platform: navigator.platform
          })
        });
        const data = await response.json();

        if (!response.ok) {
          setError(data.message || data.error || 'Invalid license key');
          return;
        }

        const { license, orgId } = data;
        setLicenseKey(key);
        localStorage.setItem('app_license_key', key);
        localStorage.setItem('app_license_permissions', JSON.stringify(license.permissions));
        localStorage.setItem('selected_org_id', orgId);
        window.dispatchEvent(new Event('storage'));
        setValidationData(license);
        setIsValidated(true);
        return;
      }

      const docRef = doc(db, 'licenses', key);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        setError('Invalid license key');
        return;
      }

      const license = docSnap.data();

      if (license.status !== "Active") {
        setError("License is " + license.status);
        logActivity(key, 'Failure', `License status: ${license.status}`);
        return;
      }

      if (license.expiryDate.toDate() < new Date()) {
        setError("License has expired");
        logActivity(key, 'Failure', 'License expired');
        return;
      }

      // Check device mapping
      const devices = license.devices || [];
      const deviceIndex = devices.findIndex((d: any) => d.id === deviceId);

      if (deviceIndex === -1) {
        if (devices.length >= (license.deviceLimit || 1)) {
          setError(`Device limit reached (${license.deviceLimit || 1}). Reset license rules in portal.`);
          logActivity(key, 'Failure', 'Device limit reached');
          return;
        }

        const newDevice = {
          id: deviceId,
          name: navigator.userAgent.substring(0, 50)
        };

        const updatedDevices = [...devices, newDevice];

        await updateDoc(docRef, { devices: updatedDevices });
        // Success! Device added.
      } else {
        // Device is already mapped. We won't update lastUsed for now to avoid breaking the hasAll rule.
      }

      setLicenseKey(key);
      localStorage.setItem('app_license_key', key);
      if (license.featurePermissions) {
        localStorage.setItem('app_license_permissions', JSON.stringify(license.featurePermissions));
      }
      if (license.orgId) {
        localStorage.setItem('selected_org_id', license.orgId);
        // Force a storage event for App.tsx if needed, or App will pick it up on next check
        window.dispatchEvent(new Event('storage'));
      }
      
      // Log successful activation
      logActivity(key, 'Success', 'License validated and activated');
      
      setValidationData(license);
      setIsValidated(true);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Connection to license server failed. Please check your internet.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (licenseKey) {
      validateLicense(licenseKey);
    } else {
      setLoading(false);
    }
  }, []);

  const handleActivate = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputKey.trim()) {
      validateLicense(inputKey.trim());
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('app_license_key');
    setLicenseKey(null);
    setIsValidated(false);
    setError(null);
    if (user) auth.signOut();
  };

  if ((loading || loadingAuth) && !user && !hasLocalDevSession()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium italic serif">Authenticating Session...</p>
        </div>
      </div>
    );
  }

  if (!isValidated && !user && !hasLocalDevSession()) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md bg-white border border-black p-10 rounded-[40px] shadow-2xl relative overflow-hidden"
        >
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary/5 rounded-full -ml-12 -mb-12" />

          <div className="relative text-center mb-10">
            <div className="w-20 h-20 bg-primary/10 rounded-3xl flex items-center justify-center mx-auto mb-6 rotate-3 hover:rotate-0 transition-transform">
              <Key className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-3xl font-bold italic serif tracking-tight mb-2">Activation Required</h1>
            <p className="text-gray-500 text-sm">Please enter your license key to unlock the application.</p>
          </div>

          <form onSubmit={handleActivate} className="space-y-6 relative">
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-gray-400 ml-4">License Key</label>
              <input 
                type="text"
                placeholder="XXXX-XXXX-XXXX-XXXX"
                value={inputKey}
                onChange={(e) => setInputKey(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 p-5 rounded-[24px] font-mono text-center tracking-[0.2em] focus:border-primary outline-none transition-all"
                required
              />
            </div>

            {error && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-2xl flex items-start gap-3"
              >
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </motion.div>
            )}

            <button 
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground py-5 rounded-[24px] font-bold text-lg hover:opacity-90 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : <ShieldCheck className="w-6 h-6" />}
              <span>Activate License</span>
            </button>
          </form>

          <p className="mt-10 text-center text-[10px] text-gray-400 uppercase tracking-widest leading-loose">
            Unique Device ID: <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">{deviceId}</span>
          </p>
        </motion.div>
        
        <p className="mt-8 text-gray-400 text-xs serif italic">
          Your personal data remains stored locally on this device.
        </p>
      </div>
    );
  }

  // Inject the validated license key context via children if needed, 
  // or use the global key from localStorage in child components.
  return (
    <>
      {children}
      {/* Show license indicator only if authenticated via license key (and not via account) */}
      {!user && licenseKey && isValidated && (
        <div className="fixed bottom-6 right-6 hidden md:flex items-center gap-2 bg-white border border-black px-4 py-2 rounded-full shadow-lg z-[1000] group">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          <span className="text-[10px] font-bold uppercase tracking-widest opacity-60">Licensed Session</span>
          <button 
            onClick={handleLogout}
            className="ml-2 p-1.5 hover:bg-red-50 text-red-500 rounded-full transition-colors opacity-0 group-hover:opacity-100"
            title="Deactivate this device"
          >
            <LogOut className="w-3 h-3" />
          </button>
        </div>
      )}
    </>
  );
}
